/**
 * Python Water — Partner Attribution
 *
 * Captures partner code + UTMs from the URL, persists them in cookie +
 * localStorage for 90 days, mirrors them into Shopify cart attributes,
 * and rewrites the direct Stripe Payment Link buttons so attributed
 * visitors land on the partner-specific Stripe Payment Link for that
 * specific product.
 *
 * Why: pythonwater.com runs two checkout paths. The normal Shopify cart
 * path is reachable via cart attributes. The product page also has a
 * "purchase" pill that opens a Stripe Payment Link directly, bypassing
 * Shopify — so partner attribution has to be carried into Stripe by
 * routing those visitors to a partner-specific Payment Link whose
 * payment_intent_data.metadata is preconfigured with partner_code.
 *
 * Each partner can be associated with multiple products. We swap a
 * direct payment button only when its href matches one of the known
 * default Payment Link URLs in PARTNER_PRODUCT_LINKS.default — buttons
 * for products without a partner-specific link are left untouched.
 *
 * Attribution rule: last valid partner click wins. 90-day window.
 * Only codes present in PARTNER_REGISTRY are accepted.
 */
(function () {
  "use strict";

  var COOKIE_NAME = "pythonwater_partner_attribution";
  var COOKIE_DAYS = 90;
  var WINDOW_MS = COOKIE_DAYS * 24 * 60 * 60 * 1000;
  var ATTRIBUTION_RULE = "last_valid_partner_click_wins";
  var attributionInitialized = false;

  // Default (non-partner) Stripe Payment Link URLs for each product
  // we currently support partner attribution on. The keys are stable
  // product identifiers used to look up partner-specific replacements.
  var PRODUCT_DEFAULT_LINKS = {
    bionic_hydrogen_bottle:
      "https://pay.pythonwater.com/b/7sYaEY0lx5aS9ch46a3AY09",
    bionic_fountain: "https://pay.pythonwater.com/b/3cI9AU9W7cDk1JPgSW3AY0a"
  };

  var PARTNER_REGISTRY = {
    DRCABRAL: {
      partner_code: "DRCABRAL",
      partner_name: "Stephen Cabral",
      partner_website: "https://stephencabral.com/",
      utm_source: "stephencabral",
      utm_medium: "partner",
      utm_campaign: "bionic_hydrogen_bottle",
      payment_links: {
        bionic_hydrogen_bottle:
          "https://pay.pythonwater.com/b/9B6eVe4BN9r89chbyC3AY0i",
        bionic_fountain:
          "https://pay.pythonwater.com/b/6oUaEY4BNgTA9cheKO3AY0j"
      }
    },
    DRBANSALCARE: {
      partner_code: "DRBANSALCARE",
      partner_name: "Dr. Arpit Bansal",
      partner_website: "",
      utm_source: "drbansal",
      utm_medium: "partner",
      utm_campaign: "bionic_hydrogen_bottle",
      payment_links: {
        // ?prefilled_promo_code auto-applies Dr. Bansal's 10% discount
        // (Stripe promo code DRBANSALCARE) at checkout.
        bionic_hydrogen_bottle:
          "https://pay.pythonwater.com/b/00wbJ23xJdHo6059qu3AY0p?prefilled_promo_code=DRBANSALCARE",
        bionic_fountain:
          "https://pay.pythonwater.com/b/6oU3cwb0b6eWdsx9qu3AY0q?prefilled_promo_code=DRBANSALCARE"
      }
    },
    FLOURISH10: {
      partner_code: "FLOURISH10",
      partner_name: "Ameriswiss Food",
      partner_website: "https://www.instagram.com/ameriswissfood/",
      utm_source: "ameriswissfood",
      utm_medium: "partner",
      utm_campaign: "bionic_hydrogen_bottle",
      payment_links: {
        bionic_hydrogen_bottle:
          "https://pay.pythonwater.com/b/eVq4gAd8jcDk2NT7im3AY0s",
        bionic_fountain:
          "https://pay.pythonwater.com/b/9B6eVe3xJ7j0ewB8mq3AY0t"
      }
    }
  };

  // Reverse lookup: default URL -> product key.
  var DEFAULT_URL_TO_PRODUCT = (function () {
    var map = {};
    Object.keys(PRODUCT_DEFAULT_LINKS).forEach(function (key) {
      map[PRODUCT_DEFAULT_LINKS[key]] = key;
    });
    return map;
  })();

  function getParams() {
    return new URLSearchParams(window.location.search);
  }

  function registeredPartner(code) {
    return Object.prototype.hasOwnProperty.call(PARTNER_REGISTRY, code)
      ? PARTNER_REGISTRY[code]
      : null;
  }

  function isDebug() {
    if (getParams().get("partner_debug") === "1") return true;
    try {
      return localStorage.getItem(COOKIE_NAME + "_debug") === "1";
    } catch (e) {
      return false;
    }
  }

  function log() {
    if (!isDebug()) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift("[Partner Attribution]");
    // eslint-disable-next-line no-console
    console.log.apply(console, args);
  }

  function setCookie(name, value, maxAgeSeconds) {
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; Max-Age=" +
      Math.floor(maxAgeSeconds) +
      "; Path=/; SameSite=Lax" +
      secure;
  }

  function deleteCookie(name) {
    var secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      name + "=; Max-Age=0; Path=/; SameSite=Lax" + secure;
  }

  function clearAttribution() {
    deleteCookie(COOKIE_NAME);
    try {
      localStorage.removeItem(COOKIE_NAME);
    } catch (e) {}
  }

  function getCookie(name) {
    var match = document.cookie.match(
      new RegExp("(^| )" + name + "=([^;]+)")
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  function parseJson(value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }

  // Milliseconds left in the attribution window, measured from the last
  // valid partner click (`partner_click_at`). Older records predate that
  // field, so fall back to `first_seen_at`, which is also click-derived.
  // Returns 0 once the window has closed.
  function windowRemainingMs(attribution) {
    if (!attribution) return 0;
    var basis = attribution.partner_click_at || attribution.first_seen_at;
    if (!basis) return 0;
    var clickedAt = Date.parse(basis);
    if (isNaN(clickedAt)) return 0;
    var remaining = WINDOW_MS - (Date.now() - clickedAt);
    return remaining > 0 ? remaining : 0;
  }

  function readStoredAttribution() {
    var parsed = null;
    var cookieValue = getCookie(COOKIE_NAME);
    if (cookieValue) parsed = parseJson(cookieValue);
    if (!parsed) {
      try {
        var localValue = localStorage.getItem(COOKIE_NAME);
        if (localValue) parsed = parseJson(localValue);
      } catch (e) {}
    }
    if (!parsed) return null;

    if (!registeredPartner(parsed.partner_code)) {
      clearAttribution();
      return null;
    }

    // The window runs from the partner click, not from the last page
    // view. localStorage carries no TTL of its own, so this read-time
    // check is the only thing that expires that copy.
    if (windowRemainingMs(parsed) <= 0) {
      log("attribution expired, clearing", parsed.partner_code);
      clearAttribution();
      return null;
    }
    return parsed;
  }

  function saveAttribution(attribution) {
    if (!attribution) return;
    // Expire against the original click instead of extending the cookie
    // by a fresh 90 days on every page view.
    var remainingMs = windowRemainingMs(attribution);
    if (remainingMs <= 0) {
      clearAttribution();
      return;
    }
    var value = JSON.stringify(attribution);
    setCookie(COOKIE_NAME, value, remainingMs / 1000);
    try {
      localStorage.setItem(COOKIE_NAME, value);
    } catch (e) {}
    if (isDebug()) {
      try {
        localStorage.setItem(COOKIE_NAME + "_debug", "1");
      } catch (e) {}
    }
    log("saved", attribution);
  }

  function buildAttribution(partnerCode, existing) {
    var params = getParams();
    var partner = PARTNER_REGISTRY[partnerCode];
    var now = new Date().toISOString();
    var firstSeenAt =
      existing && existing.partner_code === partnerCode && existing.first_seen_at
        ? existing.first_seen_at
        : now;

    return {
      partner_code: partner.partner_code,
      partner_name: partner.partner_name,
      partner_website: partner.partner_website,

      utm_source:
        params.get("utm_source") ||
        partner.utm_source ||
        partnerCode.toLowerCase(),
      utm_medium: params.get("utm_medium") || partner.utm_medium || "partner",
      utm_campaign:
        params.get("utm_campaign") || partner.utm_campaign || "",
      utm_content: params.get("utm_content") || "",
      utm_term: params.get("utm_term") || "",

      landing_page: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
      first_seen_at: firstSeenAt,
      // Start of the 90-day window. Only ever written here, i.e. only on
      // an accepted `?partner=` hit — a plain page view must not renew it.
      partner_click_at: now,
      last_seen_at: now,
      attribution_rule: ATTRIBUTION_RULE,
      attribution_window_days: String(COOKIE_DAYS)
    };
  }

  function shopifyRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return "/";
  }

  function writeCartAttributes(attribution) {
    if (!attribution || !attribution.partner_code) {
      return Promise.resolve(null);
    }
    var payload = {
      attributes: {
        partner_code: attribution.partner_code,
        partner_name: attribution.partner_name,
        partner_website: attribution.partner_website,
        partner_commission_required: "true",
        partner_commission_system: "notion",
        attribution_utm_source: attribution.utm_source,
        attribution_utm_medium: attribution.utm_medium,
        attribution_utm_campaign: attribution.utm_campaign,
        attribution_utm_content: attribution.utm_content,
        attribution_utm_term: attribution.utm_term,
        attribution_landing_page: attribution.landing_page,
        attribution_referrer: attribution.referrer,
        attribution_first_seen_at: attribution.first_seen_at,
        attribution_last_seen_at: attribution.last_seen_at,
        attribution_rule: attribution.attribution_rule
      }
    };

    return fetch(shopifyRoot() + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) {
        throw new Error("Cart attribution update failed: " + response.status);
      }
      log("cart attributes updated");
      return response.json();
    });
  }

  // Offers are explicitly enabled per anchor and per visit. Never accept
  // a promo or checkout destination from the query string. The canonical
  // default URL stays unchanged so partner routing still recognizes it.
  function resolveOfferFallback(defaultUrl, offerName) {
    if (
      offerName === "labor-day" &&
      getParams().get("offer") === "labor-day" &&
      defaultUrl === PRODUCT_DEFAULT_LINKS.bionic_hydrogen_bottle
    ) {
      return defaultUrl + "?prefilled_promo_code=WELCOMEPW";
    }
    return defaultUrl;
  }

  // A legitimate partner always wins over the optional nonpartner offer.
  // The optional second argument preserves existing one-argument callers.
  function resolvePaymentLink(defaultUrl, offerName) {
    if (!defaultUrl) return defaultUrl;
    var fallbackUrl = resolveOfferFallback(defaultUrl, offerName);
    var attribution = readStoredAttribution();
    if (!attribution || !attribution.partner_code) return fallbackUrl;

    var partner = registeredPartner(attribution.partner_code);
    if (!partner || !partner.payment_links) return fallbackUrl;

    var productKey = DEFAULT_URL_TO_PRODUCT[defaultUrl];
    if (!productKey) return fallbackUrl;

    var partnerUrl = partner.payment_links[productKey];
    if (!partnerUrl || partnerUrl.indexOf("REPLACE_WITH") !== -1) {
      // eslint-disable-next-line no-console
      console.warn(
        "[Partner Attribution] missing partner link for",
        attribution.partner_code,
        productKey
      );
      return fallbackUrl;
    }
    return partnerUrl;
  }

  function isEligibleAnchor(el) {
    if (!el || el.tagName.toLowerCase() !== "a") return false;
    if (el.hasAttribute("data-pythonwater-direct-payment-link")) return true;
    if (el.hasAttribute("data-pythonwater-default-link")) return true;
    var href = el.getAttribute("href") || "";
    return Object.prototype.hasOwnProperty.call(
      DEFAULT_URL_TO_PRODUCT,
      href
    );
  }

  function updateDirectPaymentLinks() {
    // A landing script can discover the API before DOMContentLoaded.
    // Wait until an explicit ?partner= click has been captured first.
    if (!attributionInitialized) return;
    // Build a selector that matches any anchor whose href is one of
    // our registered default Payment Link URLs.
    var selectorParts = Object.keys(DEFAULT_URL_TO_PRODUCT).map(function (
      url
    ) {
      return 'a[href="' + url + '"]';
    });
    selectorParts.push("[data-pythonwater-direct-payment-link]");
    selectorParts.push("[data-pythonwater-default-link]");
    var anchors = document.querySelectorAll(selectorParts.join(", "));

    var swapped = 0;
    anchors.forEach(function (el) {
      if (el.tagName.toLowerCase() !== "a") return;

      // Read original (default) URL via a data attribute we set the
      // first time we see the anchor — that way we always know which
      // product it represents, even after we rewrite the href.
      var originalUrl = el.getAttribute("data-pythonwater-default-link");
      if (!originalUrl) {
        var currentHref = el.getAttribute("href") || "";
        if (DEFAULT_URL_TO_PRODUCT[currentHref]) {
          originalUrl = currentHref;
          el.setAttribute("data-pythonwater-default-link", originalUrl);
        }
      }
      if (!originalUrl) return;

      el.setAttribute("data-pythonwater-direct-payment-link", "");
      var resolved = resolvePaymentLink(
        originalUrl,
        el.getAttribute("data-pythonwater-offer")
      );
      el.setAttribute("href", resolved);
      if (resolved !== originalUrl) swapped++;
    });
    log(
      "direct payment links scanned:",
      anchors.length,
      "updated destinations:",
      swapped
    );
    // Landing-page prices are shown only after checkout routing is ready.
    document.dispatchEvent(new Event("pythonwater:payment-links-refreshed"));
  }

  function isModifiedClick(event) {
    // Honour user intent to open-in-new-tab / new-window / save-link.
    return (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    );
  }

  function bindClickInterceptor() {
    var interceptClick = function (event) {
        var trigger = event.target.closest(
          "[data-pythonwater-direct-payment-link], [data-pythonwater-default-link]"
        );
        if (!trigger || !isEligibleAnchor(trigger)) return;

        var originalUrl = trigger.getAttribute("data-pythonwater-default-link");
        if (!originalUrl) {
          // Fall back to current href if we never tagged it.
          originalUrl = trigger.getAttribute("href") || "";
        }
        var offerName = trigger.getAttribute("data-pythonwater-offer");
        var paymentUrl = resolvePaymentLink(originalUrl, offerName);
        // Storage can expire or change after page load. Refresh the native
        // destination before every early return, including modified clicks.
        trigger.setAttribute("href", paymentUrl);
        if (isModifiedClick(event)) return;
        var attribution = readStoredAttribution();

        if (!attribution || paymentUrl === resolveOfferFallback(originalUrl, offerName)) {
          // Nothing to do — let native click run with the current href.
          return;
        }

        event.preventDefault();
        attribution.last_seen_at = new Date().toISOString();
        saveAttribution(attribution);

        writeCartAttributes(attribution)
          .catch(function (error) {
            // eslint-disable-next-line no-console
            console.warn(
              "[Partner Attribution] cart update before payment failed",
              error
            );
          })
          .then(function () {
            log("redirecting to partner payment link", paymentUrl);
            window.location.href = paymentUrl;
          });
      };
    document.addEventListener("click", interceptClick, true);
    document.addEventListener("auxclick", interceptClick, true);
  }

  function initAttribution() {
    var params = getParams();
    var rawPartner = params.get("partner");
    var partnerCode = rawPartner ? rawPartner.trim().toUpperCase() : null;

    var attribution = readStoredAttribution();

    if (partnerCode && registeredPartner(partnerCode)) {
      attribution = buildAttribution(partnerCode, attribution);
      saveAttribution(attribution);
    } else if (partnerCode) {
      log("invalid partner ignored", partnerCode);
    }

    if (attribution) {
      attribution.last_seen_at = new Date().toISOString();
      saveAttribution(attribution);
      writeCartAttributes(attribution).catch(function (error) {
        // eslint-disable-next-line no-console
        console.warn(
          "[Partner Attribution] failed to update cart attributes",
          error
        );
      });
    }

    attributionInitialized = true;
    updateDirectPaymentLinks();
  }

  function ready(fn) {
    if (document.readyState !== "loading") {
      fn();
    } else {
      document.addEventListener("DOMContentLoaded", fn);
    }
  }

  ready(function () {
    bindClickInterceptor();
    initAttribution();
  });

  // Expose minimal API for debugging / manual triggers from other scripts.
  window.PythonWaterPartnerAttribution = {
    get: readStoredAttribution,
    resolveLink: resolvePaymentLink,
    refreshLinks: updateDirectPaymentLinks,
    registry: PARTNER_REGISTRY,
    productDefaults: PRODUCT_DEFAULT_LINKS
  };
})();
